import React, { useEffect, useState, useCallback } from 'react';

/**
 * VideoPlayerModal - Modal component for playing video resources
 * Listens for 'open-video-modal' events dispatched by ctaHandlers.
 * Event detail is either { resourceId } (authenticated resource fetch)
 * or { videoUrl, title?, size? } for a direct/public video (e.g. the
 * landing page promo, where no auth token exists). size: 'full' widens
 * the modal to modal-full.
 */
const VideoPlayerModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [resource, setResource] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sizeClass, setSizeClass] = useState('modal-lg');

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setResource(null);
    setVideoUrl(null);
    setError(null);
  }, []);

  const fetchVideoResource = useCallback(async (resourceId) => {
    setLoading(true);
    setError(null);

    try {
      const { getAuthToken } = await import('../../utils/auth');
      const token = await getAuthToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      // Fetch resource metadata
      const resourceResponse = await fetch(`/api/resources/${resourceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!resourceResponse.ok) {
        throw new Error('Resource not found');
      }

      const resourceData = await resourceResponse.json();
      setResource(resourceData);

      // Fetch download URL for video
      const downloadResponse = await fetch(`/api/resources/${resourceId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!downloadResponse.ok) {
        throw new Error('Failed to get video URL');
      }

      const { url } = await downloadResponse.json();
      setVideoUrl(url);
    } catch (err) {
      console.error('[VideoPlayerModal] Error loading video:', err);
      setError(err.message || 'Kunne ikke laste video');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleOpenModal = (event) => {
      const { resourceId, videoUrl: directUrl, title, size } = event.detail || {};
      setIsOpen(true);
      setSizeClass(size === 'full' ? 'modal-full' : 'modal-lg');
      if (directUrl) {
        setResource(title ? { title } : null);
        setVideoUrl(directUrl);
      } else {
        fetchVideoResource(resourceId);
      }
    };

    window.addEventListener('open-video-modal', handleOpenModal);
    return () => window.removeEventListener('open-video-modal', handleOpenModal);
  }, [fetchVideoResource]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-modal-title"
    >
      <div
        className={`modal ${sizeClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header flex items-center justify-between">
          <h3 id="video-modal-title" className="font-semibold text-lg text-text-primary">
            {resource?.title || 'Video'}
          </h3>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
            aria-label="Lukk"
          >
            <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="modal-body p-0">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              <span className="ml-3 text-text-secondary">Laster video...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="w-12 h-12 text-error mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-error font-medium">{error}</p>
              <button
                onClick={handleClose}
                className="mt-4 px-4 py-2 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
              >
                Lukk
              </button>
            </div>
          )}

          {!loading && !error && videoUrl && (
            <div className="aspect-video bg-black rounded-b-2xl overflow-hidden">
              <video
                className="w-full h-full"
                controls
                autoPlay
                src={videoUrl}
              >
                Din nettleser støtter ikke videoavspilling.
              </video>
            </div>
          )}
        </div>

        {/* Footer with description (if available) */}
        {resource?.description && !loading && !error && (
          <div className="modal-footer">
            <p className="text-sm text-text-secondary">{resource.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayerModal;
